import React, { useState, useEffect, useMemo, useRef } from "react";
import { Plus, Search, Eye, Edit, Trash2, FileText, ChevronDown, Printer, CreditCard, DollarSign, CheckSquare, Calendar } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import api from "../utils/api";
import { formatMXN, formatDate, typeLabels, statusLabels, statusColors } from "../utils/format";
import SaleForm from "../components/SaleForm";
import SaleDetail from "../components/SaleDetail";
import { useSort, useBulkSelect } from "../utils/useSort";

// Cotizaciones no aparecen en el listado principal — son internas
const TYPES = ["pedido", "factura", "ticket", "importada"];
const PAYMENT_METHODS = [
  { value: "efectivo", label: "Efectivo" },
  { value: "transferencia", label: "Transferencia bancaria" },
  { value: "deposito", label: "Depósito en efectivo" },
  { value: "clave_retiro", label: "Clave de retiro en cajero" },
  { value: "tarjeta", label: "Tarjeta crédito/débito" },
];

export default function Ventas() {
  const [sales, setSales] = useState([]);
  const [filter, setFilter] = useState("todos");
  const [tab, setTab] = useState("ventas"); // "ventas" | "cotizaciones"
  const [search, setSearch] = useState("");
  const [datePeriod, setDatePeriod] = useState("todos");
  const [customDate, setCustomDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [editing, setEditing] = useState(null);
  const [showPayment, setShowPayment] = useState(null);
  const [paymentForm, setPaymentForm] = useState({ amount: 0, method: "efectivo", reference: "", notes: "" });
  const [savingPayment, setSavingPayment] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/sales");
      setSales(res.data);
    } catch { toast.error("Error al cargar ventas"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  // Cotizaciones: internas, no aparecen en ventas
  const cotizaciones = sales.filter((s) => s.type === "cotizacion");

  // Date period filter logic
  const getDateRange = (period) => {
    const now = new Date();
    const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const endOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
    switch (period) {
      case "hoy": return [startOfDay(now), endOfDay(now)];
      case "semana": { const d = new Date(now); d.setDate(d.getDate() - d.getDay()); return [startOfDay(d), endOfDay(now)]; }
      case "mes": return [new Date(now.getFullYear(), now.getMonth(), 1), endOfDay(now)];
      case "bimestre": return [new Date(now.getFullYear(), now.getMonth() - 1, 1), endOfDay(now)];
      case "trimestre": return [new Date(now.getFullYear(), now.getMonth() - 2, 1), endOfDay(now)];
      case "semestre": return [new Date(now.getFullYear(), now.getMonth() - 5, 1), endOfDay(now)];
      case "anio": return [new Date(now.getFullYear(), 0, 1), endOfDay(now)];
      case "anio_anterior": return [new Date(now.getFullYear() - 1, 0, 1), new Date(now.getFullYear() - 1, 11, 31, 23, 59, 59, 999)];
      case "dia_exacto": {
        if (!customDate) return null;
        const d = new Date(customDate + "T00:00:00");
        return [startOfDay(d), endOfDay(d)];
      }
      default: return null;
    }
  };

  const dateRange = getDateRange(datePeriod);

  const filtered = sales.filter((s) => {
    if (s.type === "cotizacion") return false;
    const matchType = filter === "todos" || s.type === filter;
    const matchSearch = !search ||
      s.folio?.toLowerCase().includes(search.toLowerCase()) ||
      s.customer_name?.toLowerCase().includes(search.toLowerCase());
    let matchDate = true;
    if (dateRange) {
      const saleDate = new Date(s.created_at);
      matchDate = saleDate >= dateRange[0] && saleDate <= dateRange[1];
    }
    return matchType && matchSearch && matchDate;
  });

  const filteredCotizaciones = cotizaciones.filter((s) =>
    !search ||
    s.folio?.toLowerCase().includes(search.toLowerCase()) ||
    s.customer_name?.toLowerCase().includes(search.toLowerCase())
  );

  const { sorted: sortedSales, SortIcon, handleSort } = useSort(filtered, "created_at", "desc");
  const { selected, toggleOne, toggleAll, clearAll, isAllSelected, isSomeSelected } = useBulkSelect(sortedSales);

  const handleBulkAction = async (action, status = "") => {
    const ids = [...selected];
    if (!ids.length) return;
    try {
      const r = await api.post("/sales/bulk-action", { ids, action, status });
      toast.success(`${r.data.updated || r.data.deleted} registros actualizados`);
      clearAll();
      load();
    } catch { toast.error("Error en acción masiva"); }
  };
  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este documento?")) return;
    await api.delete(`/sales/${id}`);
    toast.success("Documento eliminado");
    load();
  };

  const handleConvert = async (sale, targetType) => {
    try {
      await api.post(`/sales/${sale.id}/convert?target_type=${targetType}`);
      toast.success(`Convertido a ${typeLabels[targetType]}`);
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Error al convertir");
    }
  };

  const handleStatusChange = async (saleId, newStatus) => {
    try {
      await api.put(`/sales/${saleId}`, { status: newStatus });
      toast.success(`Estado actualizado a ${statusLabels[newStatus]}`);
      load();
    } catch { toast.error("Error al cambiar estado"); }
  };

  const openPaymentModal = (sale) => {
    const paid = (sale.payments || []).reduce((sum, p) => sum + (p.amount || 0), 0);
    const pending = sale.total - paid;
    setPaymentForm({ amount: pending > 0 ? pending : 0, method: "efectivo", reference: "", notes: "" });
    setShowPayment(sale);
  };

  const handlePayment = async () => {
    if (!showPayment) return;
    if (paymentForm.amount <= 0) {
      toast.error("El monto debe ser mayor a 0");
      return;
    }
    setSavingPayment(true);
    try {
      await api.post(`/sales/${showPayment.id}/payment`, {
        amount: paymentForm.amount,
        method: paymentForm.method,
        reference: paymentForm.reference,
        notes: paymentForm.notes,
        date: new Date().toISOString(),
      });
      toast.success("Pago registrado correctamente");
      setShowPayment(null);
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Error al registrar pago");
    } finally {
      setSavingPayment(false);
    }
  };

  const getPaidAmount = (sale) => (sale.payments || []).reduce((sum, p) => sum + (p.amount || 0), 0);
  const getPendingAmount = (sale) => sale.total - getPaidAmount(sale);

  return (
    <div className="space-y-6" data-testid="ventas-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Ventas</h1>
          <p className="text-slate-500 text-sm mt-1">Pedidos, facturas y tickets</p>
        </div>
        <button
          data-testid="nueva-venta-btn"
          onClick={() => { setEditing(null); setShowForm(true); }}
          className="flex items-center gap-2 bg-brand-600 hover:bg-brand-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
        >
          <Plus size={16} /> Nueva Venta
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit">
        <button onClick={() => setTab("ventas")}
          className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${tab === "ventas" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
          Ventas ({filtered.length})
        </button>
        <button onClick={() => setTab("cotizaciones")} data-testid="tab-cotizaciones"
          className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${tab === "cotizaciones" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
          Cotizaciones ({cotizaciones.length})
        </button>
      </div>

      {/* Search + Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-48">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              placeholder="Buscar folio, cliente..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>
          {tab === "ventas" && (
            <div className="flex gap-2">
              {["todos", ...TYPES].map((t) => (
                <button key={t}
                  onClick={() => setFilter(t)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    filter === t ? "bg-brand-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                >
                  {t === "todos" ? "Todos" : typeLabels[t]}
                </button>
              ))}
            </div>
          )}
        </div>
        {/* Date period filter */}
        {tab === "ventas" && (
          <div className="flex flex-wrap gap-1.5 items-center" data-testid="date-filter">
            <Calendar size={13} className="text-slate-400 mr-1" />
            {[
              { key: "todos", label: "Todo" },
              { key: "hoy", label: "Hoy" },
              { key: "semana", label: "Semana" },
              { key: "mes", label: "Mes" },
              { key: "bimestre", label: "Bimestre" },
              { key: "trimestre", label: "Trimestre" },
              { key: "semestre", label: "Semestre" },
              { key: "anio", label: "Este año" },
              { key: "anio_anterior", label: "Año anterior" },
              { key: "dia_exacto", label: "Dia exacto" },
            ].map(p => (
              <button key={p.key} onClick={() => setDatePeriod(p.key)} data-testid={`date-${p.key}`}
                className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors ${
                  datePeriod === p.key ? "bg-slate-800 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                }`}>{p.label}</button>
            ))}
            {datePeriod === "dia_exacto" && (
              <input type="date" value={customDate} onChange={e => setCustomDate(e.target.value)}
                className="border border-slate-200 rounded-md px-2 py-1 text-xs ml-1" data-testid="date-exact-input" />
            )}
            {datePeriod !== "todos" && (
              <span className="text-[10px] text-slate-400 ml-2">
                {filtered.length} resultado{filtered.length !== 1 ? "s" : ""}
                {dateRange && ` | ${dateRange[0].toLocaleDateString("es-MX")} - ${dateRange[1].toLocaleDateString("es-MX")}`}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Tabla Ventas */}
      {tab === "ventas" && (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {/* Bulk action bar */}
        {selected.size > 0 && (
          <div className="px-4 py-2.5 bg-brand-50 border-b border-brand-200 flex items-center gap-3 flex-wrap">
            <span className="text-sm font-medium text-brand-700">{selected.size} seleccionado(s)</span>
            <button onClick={() => handleBulkAction("mark_paid")}
              className="px-3 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md text-xs font-medium transition-colors">
              Marcar como Pagado
            </button>
            <button onClick={() => handleBulkAction("mark_status", "confirmado")}
              className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-xs font-medium transition-colors">
              Marcar Confirmado
            </button>
            <button onClick={() => handleBulkAction("mark_status", "enviado")}
              className="px-3 py-1 bg-violet-600 hover:bg-violet-700 text-white rounded-md text-xs font-medium transition-colors">
              Marcar Enviado
            </button>
            <button onClick={() => handleBulkAction("mark_status", "entregado")}
              className="px-3 py-1 bg-slate-600 hover:bg-slate-700 text-white rounded-md text-xs font-medium transition-colors">
              Marcar Entregado
            </button>
            <button onClick={() => { if (window.confirm(`¿Eliminar ${selected.size} documentos?`)) handleBulkAction("delete"); }}
              className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white rounded-md text-xs font-medium transition-colors">
              Eliminar
            </button>
            <button onClick={clearAll} className="text-xs text-slate-500 hover:underline ml-auto">Deseleccionar</button>
          </div>
        )}
        <table className="w-full text-sm" data-testid="sales-table">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-4 py-3 w-8">
                <input type="checkbox" checked={isAllSelected} ref={el => el && (el.indeterminate = isSomeSelected)}
                  onChange={toggleAll} className="rounded border-slate-300 cursor-pointer" />
              </th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort("folio")}>
                Folio <SortIcon col="folio" />
              </th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort("type")}>
                Tipo <SortIcon col="type" />
              </th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort("customer_name")}>
                Cliente <SortIcon col="customer_name" />
              </th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort("created_at")}>
                Fecha <SortIcon col="created_at" />
              </th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort("total")}>
                Total <SortIcon col="total" />
              </th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort("status")}>
                Estatus <SortIcon col="status" />
              </th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Canal</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="text-center py-12 text-slate-400">Cargando...</td></tr>
            ) : sortedSales.length === 0 ? (
              <tr><td colSpan={9} className="text-center py-12 text-slate-400">Sin documentos</td></tr>
            ) : sortedSales.map((s) => (
              <tr key={s.id} className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${selected.has(s.id) ? "bg-brand-50" : ""}`}>
                <td className="px-4 py-3">
                  <input type="checkbox" checked={selected.has(s.id)} onChange={() => toggleOne(s.id)}
                    className="rounded border-slate-300 cursor-pointer" />
                </td>
                <td className="px-4 py-3 font-mono text-xs font-medium text-slate-800">{s.folio}</td>
                <td className="px-4 py-3">
                  <span className="bg-slate-100 text-slate-700 text-xs px-2 py-0.5 rounded-full font-medium">
                    {typeLabels[s.type] || s.type}
                  </span>
                </td>
                <td className="px-4 py-3 text-slate-700">{s.customer_name || <span className="text-slate-400 italic">Sin cliente</span>}</td>
                <td className="px-4 py-3 text-slate-500 text-xs">{formatDate(s.created_at)}</td>
                <td className="px-4 py-3 font-semibold text-slate-900">{formatMXN(s.total)}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColors[s.status] || "bg-slate-100"}`}>
                    {statusLabels[s.status] || s.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-xs text-slate-500">{
                  {directo: "Directo", redes_sociales: "Redes", expo: "Expo", menudeo: "Menudeo", mayoreo: "Mayoreo", tienda_linea: "Online"}[s.sales_channel] || ""
                }</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1 justify-end">
                    <button onClick={() => setShowDetail(s)} className="p-1.5 hover:bg-slate-100 rounded-md transition-colors" title="Ver">
                      <Eye size={14} className="text-slate-500" />
                    </button>
                    <button onClick={() => openPaymentModal(s)}
                      className={`p-1.5 rounded-md transition-colors ${getPendingAmount(s) > 0 ? "hover:bg-emerald-50" : "hover:bg-slate-100"}`}
                      title={getPendingAmount(s) > 0 ? "Registrar pago" : "Ver pagos"}>
                      <DollarSign size={14} className={getPendingAmount(s) > 0 ? "text-emerald-600" : "text-slate-400"} />
                    </button>
                    <button onClick={() => { setEditing(s); setShowForm(true); }} className="p-1.5 hover:bg-slate-100 rounded-md transition-colors" title="Editar">
                      <Edit size={14} className="text-slate-500" />
                    </button>
                    {s.type === "pedido" && (
                      <button onClick={() => handleConvert(s, "factura")}
                        className="px-2 py-1 bg-violet-50 hover:bg-violet-100 text-violet-700 rounded text-xs font-medium transition-colors"
                        title="Convertir a Factura">→ Factura</button>
                    )}
                    {(s.type === "pedido" || s.type === "importada") && !["enviado", "entregado", "cancelado"].includes(s.status) && (
                      <>
                        <button onClick={() => handleStatusChange(s.id, "enviado")}
                          className="px-2 py-1 bg-amber-50 hover:bg-amber-100 text-amber-700 rounded text-xs font-medium transition-colors"
                          data-testid={`btn-enviado-${s.id}`}
                          title="Marcar como enviado">Enviado</button>
                        <button onClick={() => handleStatusChange(s.id, "entregado")}
                          className="px-2 py-1 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded text-xs font-medium transition-colors"
                          data-testid={`btn-entregado-${s.id}`}
                          title="Marcar como entregado">Entregado</button>
                      </>
                    )}
                    <button onClick={() => handleDelete(s.id)} className="p-1.5 hover:bg-red-50 rounded-md transition-colors" title="Eliminar">
                      <Trash2 size={14} className="text-red-400" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      {/* Tabla Cotizaciones */}
      {tab === "cotizaciones" && (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 bg-slate-50">
          <p className="text-xs text-slate-500">Las cotizaciones son internas y no afectan tus ventas. Conviértelas a Pedido cuando el cliente confirme.</p>
        </div>
        <table className="w-full text-sm" data-testid="cotizaciones-table">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Folio</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Cliente</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Fecha</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Válida hasta</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Total</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {filteredCotizaciones.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-12 text-slate-400">Sin cotizaciones</td></tr>
            ) : filteredCotizaciones.map((s) => (
              <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                <td className="px-4 py-3 font-mono text-xs font-medium text-slate-800">{s.folio}</td>
                <td className="px-4 py-3 text-slate-700">{s.customer_name || <span className="text-slate-400 italic">Sin cliente</span>}</td>
                <td className="px-4 py-3 text-slate-500 text-xs">{formatDate(s.created_at)}</td>
                <td className="px-4 py-3 text-xs">
                  {s.valid_until
                    ? <span className={new Date(s.valid_until) < new Date() ? "text-red-500 font-medium" : "text-slate-600"}>{s.valid_until.slice(0,10)}</span>
                    : <span className="text-slate-300">—</span>
                  }
                </td>
                <td className="px-4 py-3 font-semibold text-slate-900">{formatMXN(s.total)}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1 justify-end">
                    <button onClick={() => setShowDetail(s)} className="p-1.5 hover:bg-slate-100 rounded-md" title="Ver">
                      <Eye size={14} className="text-slate-500" />
                    </button>
                    <button onClick={() => { setEditing(s); setShowForm(true); }} className="p-1.5 hover:bg-slate-100 rounded-md" title="Editar">
                      <Edit size={14} className="text-slate-500" />
                    </button>
                    <button onClick={() => handleConvert(s, "pedido")}
                      className="px-2 py-1 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded text-xs font-medium transition-colors"
                      title="Convertir a Pedido">→ Pedido</button>
                    <button onClick={() => handleDelete(s.id)} className="p-1.5 hover:bg-red-50 rounded-md" title="Eliminar">
                      <Trash2 size={14} className="text-red-400" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <SaleForm
          sale={editing}
          onClose={() => setShowForm(false)}
          onSave={() => { setShowForm(false); load(); }}
        />
      )}

      {/* Detail Modal */}
      {showDetail && (
        <SaleDetail
          sale={showDetail}
          onClose={() => setShowDetail(null)}
          onRefresh={load}
        />
      )}

      {/* Payment Modal */}
      {showPayment && (
        <Dialog open={true} onOpenChange={() => setShowPayment(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <DollarSign size={20} className="text-emerald-600" />
                Registrar Pago — {showPayment.folio}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 mt-2">
              {/* Sale Summary */}
              <div className="bg-slate-50 rounded-lg p-3 space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Total de la venta:</span>
                  <span className="font-semibold">{formatMXN(showPayment.total)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Ya pagado:</span>
                  <span className="text-emerald-600 font-medium">{formatMXN(getPaidAmount(showPayment))}</span>
                </div>
                <div className="flex justify-between border-t border-slate-200 pt-1 mt-1">
                  <span className="text-slate-700 font-medium">Pendiente:</span>
                  <span className={`font-bold ${getPendingAmount(showPayment) > 0 ? "text-amber-600" : "text-emerald-600"}`}>
                    {getPendingAmount(showPayment) > 0 ? formatMXN(getPendingAmount(showPayment)) : "✓ Pagado"}
                  </span>
                </div>
              </div>

              {/* Previous payments */}
              {(showPayment.payments || []).length > 0 && (
                <div>
                  <div className="text-xs font-semibold text-slate-500 uppercase mb-2">Pagos anteriores</div>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {(showPayment.payments || []).map((p, i) => (
                      <div key={i} className="flex justify-between items-center text-sm bg-emerald-50 rounded-lg px-3 py-2">
                        <div>
                          <span className="font-medium text-emerald-800">{formatMXN(p.amount)}</span>
                          <span className="text-emerald-600 text-xs ml-2">
                            {PAYMENT_METHODS.find(m => m.value === p.method)?.label || p.method}
                          </span>
                        </div>
                        <span className="text-xs text-emerald-600">{formatDate(p.date)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* New payment form */}
              {getPendingAmount(showPayment) > 0 && (
                <>
                  <div className="border-t border-slate-200 pt-4">
                    <div className="text-xs font-semibold text-slate-500 uppercase mb-3">Nuevo pago</div>
                    
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Monto *</label>
                        <input
                          type="number"
                          value={paymentForm.amount}
                          onChange={(e) => setPaymentForm({ ...paymentForm, amount: parseFloat(e.target.value) || 0 })}
                          min="0"
                          step="0.01"
                          className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Método de pago *</label>
                        <select
                          value={paymentForm.method}
                          onChange={(e) => setPaymentForm({ ...paymentForm, method: e.target.value })}
                          className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        >
                          {PAYMENT_METHODS.map((m) => (
                            <option key={m.value} value={m.value}>{m.label}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Referencia / Folio de transacción</label>
                        <input
                          type="text"
                          value={paymentForm.reference}
                          onChange={(e) => setPaymentForm({ ...paymentForm, reference: e.target.value })}
                          placeholder="Ej: 1234567890"
                          className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Notas</label>
                        <input
                          type="text"
                          value={paymentForm.notes}
                          onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                          placeholder="Observaciones opcionales"
                          className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2 pt-2">
                    <button
                      onClick={() => setShowPayment(null)}
                      className="flex-1 border border-slate-200 hover:bg-slate-50 text-slate-600 py-2.5 rounded-lg text-sm font-medium transition-colors"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={handlePayment}
                      disabled={savingPayment || paymentForm.amount <= 0}
                      className="flex-1 flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white py-2.5 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50"
                    >
                      {savingPayment ? (
                        <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <DollarSign size={16} />
                      )}
                      Registrar pago
                    </button>
                  </div>
                </>
              )}

              {getPendingAmount(showPayment) <= 0 && (
                <button
                  onClick={() => setShowPayment(null)}
                  className="w-full border border-slate-200 hover:bg-slate-50 text-slate-600 py-2.5 rounded-lg text-sm font-medium transition-colors"
                >
                  Cerrar
                </button>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
